(() => {
  const root = document.documentElement;
  const liveRegion = () => document.getElementById('ReeflineLiveRegion');
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const controllers = new WeakMap();
  const modalSessions = new WeakMap();
  const inertStates = new WeakMap();
  let announcementFrame;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const hoverVideoState = new WeakMap();
  const revealAnimations = new WeakMap();
  const revealStyles = new WeakMap();
  const pressAnimations = new WeakMap();
  const pressStyles = new WeakMap();
  const accordionAnimations = new WeakMap();
  const motionMini = () => window.ReeflineMotionMini?.animate;
  const motionAllowed = () => root.dataset.motion !== 'off' && !prefersReducedMotion.matches && !navigator.connection?.saveData;
  const rememberStyles = (node, properties) => Object.fromEntries(properties.map((name) => [name, [node.style.getPropertyValue(name), node.style.getPropertyPriority(name)]]));
  const restoreStyles = (node, saved) => {
    if (!saved) return;
    Object.entries(saved).forEach(([name, [value, priority]]) => {
      if (value) node.style.setProperty(name, value, priority);
      else node.style.removeProperty(name);
    });
  };

  const cardMotionAllowed = motionAllowed;
  const hoverVideo = (media) => media?.querySelector?.('[data-product-hover-video]');

  // The crossfade lasts 420ms (see --duration-media); pause only once the
  // poster is fully back so the last frame never pops. Playback resumes from
  // where it stopped on the next hover instead of restarting.
  const VIDEO_FADE_MS = 440;

  const attachVideoSources = (video) => {
    let attached = false;
    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
      attached = true;
    });
    return attached;
  };

  const stopCardVideo = (media, reset = false) => {
    const video = hoverVideo(media);
    const state = hoverVideoState.get(media);
    if (state?.startTimer) window.clearTimeout(state.startTimer);
    if (state?.stopTimer) window.clearTimeout(state.stopTimer);
    if (state?.reveal) video?.removeEventListener('playing', state.reveal);
    media?.classList.remove('is-video-playing');
    if (!video) return;
    const stopTimer = window.setTimeout(() => {
      video.pause();
      if (reset) {
        try { video.currentTime = 0; } catch (_) { /* Media may not have loaded yet. */ }
      }
    }, VIDEO_FADE_MS);
    hoverVideoState.set(media, { stopTimer });
  };

  const startCardVideo = (media) => {
    if (!finePointer.matches || !cardMotionAllowed()) return;
    const video = hoverVideo(media);
    if (!video) return;
    const state = hoverVideoState.get(media);
    if (state?.startTimer) window.clearTimeout(state.startTimer);
    if (state?.stopTimer) window.clearTimeout(state.stopTimer);
    if (state?.reveal) video.removeEventListener('playing', state.reveal);
    const startTimer = window.setTimeout(() => {
      if (!finePointer.matches || !cardMotionAllowed()) {
        stopCardVideo(media);
        return;
      }
      attachVideoSources(video);
      if (video.readyState === 0) video.load();
      // Only crossfade once real frames are being painted; a play() promise can
      // resolve before decoding, which used to flash the poster.
      const reveal = () => {
        video.removeEventListener('playing', reveal);
        if (hoverVideoState.get(media)?.reveal !== reveal) return;
        media.classList.add('is-video-playing');
      };
      hoverVideoState.set(media, { reveal });
      video.play().then(() => {
        if (video.readyState >= 3) reveal();
        else video.addEventListener('playing', reveal, { once: true });
      }).catch(() => stopCardVideo(media, false));
    }, 180);
    hoverVideoState.set(media, { startTimer });
  };

  // Warm the hover media while the card is on screen so the first hover plays
  // immediately: attach sources with metadata preload and hydrate the secondary
  // image. Fine pointers only, never on Save-Data or reduced motion.
  let warmObserver;
  const warmCardMedia = (media) => {
    if (media.dataset.mediaWarmed === 'true') return;
    media.dataset.mediaWarmed = 'true';
    const card = media.closest('[data-product-card]');
    const image = card?.querySelector('[data-secondary-image]') || media.querySelector('[data-secondary-image]');
    if (image) hydrateSecondaryImage({ type: 'focusin', target: image });
    const video = hoverVideo(media);
    if (!video || !cardMotionAllowed()) return;
    if (attachVideoSources(video)) {
      video.preload = 'metadata';
      video.load();
    }
  };
  const observeCardMedia = (scope = document) => {
    if (!finePointer.matches || !cardMotionAllowed() || !('IntersectionObserver' in window)) return;
    warmObserver ||= new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        warmObserver.unobserve(entry.target);
        warmCardMedia(entry.target);
      });
    }, { rootMargin: '160px 0px', threshold: .1 });
    scope.querySelectorAll('[data-product-hover-media]').forEach((media) => {
      if (media.dataset.mediaWarmed === 'true') return;
      warmObserver.observe(media);
    });
  };

  const announce = (message) => {
    const region = liveRegion();
    if (!region || !message) return;
    window.cancelAnimationFrame(announcementFrame);
    region.textContent = '';
    announcementFrame = window.requestAnimationFrame(() => { region.textContent = String(message); });
  };

  const hydrateSecondaryImage = (event) => {
    if (event.type === 'pointerover' && !finePointer.matches) return;
    const card = event.target.closest?.('[data-product-card]');
    const image = event.target.matches?.('[data-secondary-image]') ? event.target : card?.querySelector('[data-secondary-image]');
    if (!image || image.dataset.hydrated === 'true') return;
    image.dataset.hydrated = 'true';
    if (image.dataset.srcset) image.srcset = image.dataset.srcset;
    if (image.dataset.src) image.src = image.dataset.src;
    delete image.dataset.srcset;
    delete image.dataset.src;
  };

  const isVisible = (element) => {
    const styles = window.getComputedStyle(element);
    return !element.hidden && element.getAttribute('tabindex') !== '-1' && styles.display !== 'none' && styles.visibility !== 'hidden' && element.getClientRects().length > 0;
  };

  const getFocusable = (container) => Array.from(container.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  )).filter(isVisible);

  const focusElement = (element) => {
    if (!element?.isConnected) return false;
    try { element.focus({ preventScroll: true }); } catch (_) { element.focus(); }
    return document.activeElement === element;
  };

  const resolveFocusTarget = (container, target) => {
    if (typeof target === 'function') return target(container);
    if (typeof target === 'string') return container.querySelector(target);
    return target;
  };

  class FocusTrap {
    constructor(container, returnTargetOrOptions, initialFocus) {
      this.container = container;
      const options = returnTargetOrOptions && !returnTargetOrOptions.nodeType
        ? returnTargetOrOptions
        : { returnTarget: returnTargetOrOptions, initialFocus };
      this.returnTarget = options.returnTarget;
      this.initialFocus = resolveFocusTarget(container, options.initialFocus);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      document.addEventListener('keydown', this.onKeyDown);
      document.addEventListener('focusin', this.onFocusIn);
      const first = this.initialFocus || getFocusable(container)[0] || container;
      focusElement(first);
    }
    onKeyDown(event) {
      if (event.key !== 'Tab') return;
      const focusable = getFocusable(this.container);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!this.container.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    }
    onFocusIn(event) {
      if (this.container.contains(event.target)) return;
      focusElement(this.initialFocus || getFocusable(this.container)[0] || this.container);
    }
    destroy({ restore = true } = {}) {
      document.removeEventListener('keydown', this.onKeyDown);
      document.removeEventListener('focusin', this.onFocusIn);
      if (restore) focusElement(this.returnTarget);
    }
  }

  const modalBranches = (container) => {
    const branches = [];
    let current = container;
    while (current?.parentElement && current !== document.body) {
      Array.from(current.parentElement.children).forEach((sibling) => {
        if (
          sibling !== current
          && !sibling.hasAttribute('data-modal-live-region')
          && !['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)
        ) branches.push(sibling);
      });
      current = current.parentElement;
    }
    return branches;
  };

  const lockInert = (element) => {
    const state = inertStates.get(element);
    if (state) {
      state.count += 1;
      return;
    }
    inertStates.set(element, { count: 1, previous: element.inert });
    element.inert = true;
  };

  const unlockInert = (element) => {
    const state = inertStates.get(element);
    if (!state) return;
    state.count -= 1;
    if (state.count > 0) return;
    element.inert = state.previous;
    inertStates.delete(element);
  };

  const deactivateModal = (container, { restore = true } = {}) => {
    const session = modalSessions.get(container);
    if (!session) return;
    const returnTarget = session.trap.returnTarget;
    session.trap.destroy({ restore: false });
    session.branches.forEach(unlockInert);
    modalSessions.delete(container);
    if (restore) focusElement(returnTarget);
  };

  const activateModal = (container, { launcher, initialFocus } = {}) => {
    if (!container) return null;
    deactivateModal(container, { restore: false });
    const branches = modalBranches(container);
    branches.forEach(lockInert);
    const trap = new FocusTrap(container, { returnTarget: launcher, initialFocus });
    modalSessions.set(container, { branches, trap });
    return trap;
  };

  class DisclosureController {
    constructor(element) {
      this.element = element;
      this.button = element.querySelector('[data-disclosure-button]');
      this.panel = element.querySelector('[data-disclosure-panel]');
      if (!this.button || !this.panel) return;
      this.onToggle = () => this.toggle();
      this.onKeyDown = (event) => { if (event.key === 'Escape') this.close(); };
      this.button.addEventListener('click', this.onToggle);
      this.element.addEventListener('keydown', this.onKeyDown);
    }
    get open() { return this.button.getAttribute('aria-expanded') === 'true'; }
    toggle() { this.open ? this.close() : this.openPanel(); }
    openPanel({ editorSelected = false } = {}) {
      document.querySelectorAll('[data-disclosure]').forEach((node) => {
        if (node !== this.element) controllers.get(node)?.close?.({ restore: false });
      });
      this.button.setAttribute('aria-expanded', 'true');
      this.panel.hidden = false;
      this.element.classList.add('is-open');
      if (editorSelected) this.element.dataset.editorSelected = 'true';
    }
    close({ restore = true } = {}) {
      if (!this.open) return;
      this.button.setAttribute('aria-expanded', 'false');
      this.panel.hidden = true;
      this.element.classList.remove('is-open');
      delete this.element.dataset.editorSelected;
      if (restore) focusElement(this.button);
    }
    destroy() {
      this.button?.removeEventListener('click', this.onToggle);
      this.element.removeEventListener('keydown', this.onKeyDown);
    }
  }

  class DialogController {
    constructor(element) {
      this.element = element;
      this.id = element.id;
      this.launchers = Array.from(document.querySelectorAll(`[aria-controls="${CSS.escape(this.id)}"]`));
      this.closers = Array.from(element.querySelectorAll('[data-dialog-close]'));
      this.onKeyDown = (event) => { if (event.key === 'Escape') this.close(); };
      this.onDocumentKeyDown = (event) => {
        if (event.key !== 'Escape' || !this.isOpen) return;
        this.onDismiss?.('escape');
        this.close();
      };
      this.onLauncherClick = (event) => this.open(event.currentTarget);
      this.onCloserClick = () => this.close();
      this.openFrame = null;
      this.trapFrame = null;
      this.launchers.forEach((button) => button.addEventListener('click', this.onLauncherClick));
      this.closers.forEach((button) => button.addEventListener('click', this.onCloserClick));
      document.addEventListener('keydown', this.onDocumentKeyDown);
    }
    addLauncher(launcher) {
      if (!launcher || this.launchers.includes(launcher)) return;
      this.launchers.push(launcher);
      launcher.addEventListener('click', this.onLauncherClick);
    }
    setDismissHandler(handler) { this.onDismiss = handler; }
    open(launcher, returnTarget = launcher) {
      if (this.isOpen) return;
      window.cancelAnimationFrame(this.openFrame);
      window.cancelAnimationFrame(this.trapFrame);
      this.launcher = launcher;
      this.returnTarget = returnTarget;
      this.isOpen = true;
      this.element.hidden = false;
      document.body.classList.add('is-locked');
      launcher?.setAttribute('aria-expanded', 'true');
      this.element.querySelector('[data-dialog-close]')?.focus?.();
      this.openFrame = window.requestAnimationFrame(() => {
        if (!this.isOpen || !this.element.isConnected) return;
        this.element.classList.add('is-open');
        this.trapFrame = window.requestAnimationFrame(() => {
          if (!this.isOpen || !this.element.isConnected || !this.element.classList.contains('is-open')) return;
          this.trap = activateModal(this.element, {
            launcher: returnTarget,
            initialFocus: this.element.querySelector('[data-dialog-initial-focus], [aria-labelledby][tabindex="-1"]')
          });
        });
      });
    }
    close({ restore = true, immediate = false } = {}) {
      if (!this.isOpen) return;
      this.isOpen = false;
      window.cancelAnimationFrame(this.openFrame);
      window.cancelAnimationFrame(this.trapFrame);
      this.openFrame = null;
      this.trapFrame = null;
      this.element.classList.remove('is-open');
      document.body.classList.remove('is-locked');
      this.launcher?.setAttribute('aria-expanded', 'false');
      deactivateModal(this.element, { restore });
      this.trap = null;
      const hide = () => { if (!this.element.classList.contains('is-open')) this.element.hidden = true; };
      if (immediate || prefersReducedMotion.matches) hide();
      else window.setTimeout(hide, 300);
    }
    destroy() {
      this.close({ restore: false, immediate: true });
      this.launchers.forEach((button) => button.removeEventListener('click', this.onLauncherClick));
      this.closers.forEach((button) => button.removeEventListener('click', this.onCloserClick));
      document.removeEventListener('keydown', this.onDocumentKeyDown);
    }
  }

  class GiftCardRecipientController {
    constructor(element) {
      this.element = element;
      this.toggle = element.querySelector('[data-gift-card-recipient-toggle]');
      this.fields = element.querySelector('[data-gift-card-recipient-fields]');
      this.email = element.querySelector('[data-gift-card-recipient-email]');
      this.controls = Array.from(this.fields?.querySelectorAll('input, select, textarea') || []);
      if (!this.toggle || !this.fields) return;
      this.onChange = () => this.sync();
      this.toggle.addEventListener('change', this.onChange);
      this.sync();
    }
    sync() {
      const active = Boolean(this.toggle?.checked);
      if (!this.fields) return;
      this.fields.hidden = !active;
      this.toggle.setAttribute('aria-expanded', String(active));
      this.controls.forEach((control) => { control.disabled = !active; });
      if (this.email) this.email.required = active;
    }
    destroy() {
      this.toggle?.removeEventListener('change', this.onChange);
    }
  }

  const factories = [
    ['[data-disclosure]', (node) => new DisclosureController(node)],
    ['[data-dialog]', (node) => new DialogController(node)],
    ['[data-gift-card-recipient]', (node) => new GiftCardRecipientController(node)]
  ];

  const initialize = (scope = document) => {
    factories.forEach(([selector, factory]) => {
      scope.querySelectorAll(selector).forEach((node) => {
        if (controllers.has(node)) return;
        controllers.set(node, factory(node));
      });
    });
    initializeReveals(scope);
    observeCardMedia(scope);
  };

  const openDialog = (id, launcher, { onDismiss, returnTarget = launcher } = {}) => {
    const element = typeof id === 'string' ? document.getElementById(id) : id;
    if (!element) return false;
    let controller = controllers.get(element);
    if (!controller) {
      controller = new DialogController(element);
      controllers.set(element, controller);
    }
    if (onDismiss) controller.setDismissHandler(onDismiss);
    if (launcher?.getAttribute?.('aria-controls') === element.id) controller.addLauncher(launcher);
    controller.open(launcher, returnTarget);
    return true;
  };

  const setDialogDismissHandler = (id, handler) => {
    const element = typeof id === 'string' ? document.getElementById(id) : id;
    const controller = element && controllers.get(element);
    if (!controller) return false;
    controller.setDismissHandler(handler);
    return true;
  };

  const closeDialog = (id, options = {}) => {
    const element = typeof id === 'string' ? document.getElementById(id) : id;
    if (!element) return false;
    controllers.get(element)?.close(options);
    return true;
  };

  const destroy = (scope) => {
    scope.querySelectorAll('[data-product-hover-media]').forEach((media) => warmObserver?.unobserve(media));
    factories.forEach(([selector]) => {
      scope.querySelectorAll(selector).forEach((node) => {
        controllers.get(node)?.destroy?.();
        controllers.delete(node);
      });
    });
    revealNodes(scope).forEach((node) => {
      revealObserver?.unobserve(node);
      revealAnimations.get(node)?.cancel();
      revealAnimations.delete(node);
      restoreStyles(node, revealStyles.get(node));
      revealStyles.delete(node);
      node.classList.remove('is-pending');
      delete node.dataset.revealReady;
    });
    scope.querySelectorAll('.reef-footer .newsletter-form__field button, .reef-footer__social a').forEach((node) => clearPress(node));
  };

  let revealObserver;
  const revealNodes = (scope) => [
    ...(scope.matches?.('[data-reveal]') ? [scope] : []),
    ...scope.querySelectorAll('[data-reveal]')
  ];
  const revealImmediately = (scope = document) => {
    revealNodes(scope).forEach((node) => {
      revealObserver?.unobserve(node);
      revealAnimations.get(node)?.cancel();
      revealAnimations.delete(node);
      restoreStyles(node, revealStyles.get(node));
      revealStyles.delete(node);
      node.dataset.revealReady = 'true';
      node.classList.remove('is-pending');
    });
  };

  const revealNode = (node) => {
    revealObserver?.unobserve(node);
    node.classList.remove('is-pending');
    if (!motionAllowed() || !motionMini()) return;
    const travel = getComputedStyle(node).getPropertyValue('--motion-travel').trim() || '20px';
    const editorialSection = node.closest('.section-rich-text, .section-editorial-grid, .section-image-with-text, .section-science-visualizer, .section-story-scroller, .section-impact-bookend, .section-featured-blog');
    const siblings = editorialSection && root.dataset.motion === 'expressive'
      ? Array.from(node.parentElement?.children || []).filter((sibling) => sibling.matches?.('[data-reveal]'))
      : [];
    const delay = Math.min(Math.max(0, siblings.indexOf(node)) * 0.04, 0.12);
    try {
      revealStyles.set(node, rememberStyles(node, ['opacity', 'transform']));
      const animation = motionMini()(node, {
        opacity: [0, 1],
        transform: [`translate3d(0, ${travel}, 0)`, 'translate3d(0, 0, 0)']
      }, { duration: 0.5, delay, ease: [.165, .84, .44, 1] });
      revealAnimations.set(node, animation);
      animation.finished.then(() => {
        if (revealAnimations.get(node) !== animation) return;
        animation.cancel();
        revealAnimations.delete(node);
        restoreStyles(node, revealStyles.get(node));
        revealStyles.delete(node);
      }).catch(() => {});
    } catch (_) {
      revealAnimations.delete(node);
      restoreStyles(node, revealStyles.get(node));
      revealStyles.delete(node);
    }
  };

  const initializeReveals = (scope) => {
    if (!motionAllowed() || !motionMini() || !('IntersectionObserver' in window) || !Element.prototype.animate) {
      revealImmediately(scope);
      return;
    }
    revealObserver ||= new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        revealNode(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: .08 });
    revealNodes(scope).forEach((node) => {
      if (node.dataset.revealReady) return;
      node.dataset.revealReady = 'true';
      if (node.getBoundingClientRect().top < window.innerHeight) return;
      node.classList.add('is-pending');
      revealObserver.observe(node);
    });
  };

  const handleMotionPreference = (event) => {
    if (!event.matches && motionAllowed()) return;
    revealObserver?.disconnect();
    revealObserver = null;
    revealImmediately(document);
    document.querySelectorAll('.reef-footer .newsletter-form__field button, .reef-footer__social a').forEach((node) => clearPress(node));
  };

  const pressTargets = '.reef-footer .newsletter-form__field button, .reef-footer__social a';
  const clearPress = (node) => {
    pressAnimations.get(node)?.cancel();
    pressAnimations.delete(node);
    restoreStyles(node, pressStyles.get(node));
    pressStyles.delete(node);
  };
  document.addEventListener('pointerup', (event) => {
    const target = event.target.closest?.(pressTargets);
    if (!target || !motionAllowed() || !motionMini() || !Element.prototype.animate) return;
    try {
      clearPress(target);
      pressStyles.set(target, rememberStyles(target, ['transform']));
      const animation = motionMini()(target, { transform: ['scale(.96)', 'scale(1)'] }, {
        duration: .22,
        ease: [.165, .84, .44, 1]
      });
      pressAnimations.set(target, animation);
      animation.finished.then(() => {
        if (pressAnimations.get(target) === animation) clearPress(target);
      }).catch(() => {});
    } catch (_) { clearPress(target); }
  }, { passive: true });

  // One height transition for native content accordions throughout the theme.
  // The native details element remains usable when JavaScript or motion is off.
  const accordionSelector = '.buybox-accordion__item, .launch-faq__item, .facets__group, .cart-drawer__note, .pickup-availability details';
  const setAccordionOpen = (details, shouldOpen) => {
    const active = accordionAnimations.get(details);
    if (active) {
      active.animation.cancel();
      active.complete();
    }
    if (details.open === shouldOpen) return;
    if (!motionAllowed() || !Element.prototype.animate) {
      details.open = shouldOpen;
      return;
    }
    const summary = details.querySelector(':scope > summary');
    if (!summary) {
      details.open = shouldOpen;
      return;
    }
    const from = details.getBoundingClientRect().height;
    if (shouldOpen) details.open = true;
    details.classList.toggle('is-collapsing', !shouldOpen);
    const border = parseFloat(getComputedStyle(details).borderTopWidth) + parseFloat(getComputedStyle(details).borderBottomWidth);
    const to = shouldOpen ? details.scrollHeight + border : summary.getBoundingClientRect().height + border;
    details.style.overflow = 'hidden';
    const animation = details.animate([{ height: `${from}px` }, { height: `${to}px` }], {
      duration: 320,
      easing: 'cubic-bezier(.22, 1, .36, 1)'
    });
    const complete = () => {
      if (accordionAnimations.get(details)?.animation !== animation) return;
      if (!shouldOpen) details.open = false;
      details.classList.remove('is-collapsing');
      details.style.removeProperty('overflow');
      accordionAnimations.delete(details);
    };
    accordionAnimations.set(details, { animation, complete, shouldOpen });
    animation.onfinish = complete;
  };
  document.addEventListener('click', (event) => {
    const summary = event.target.closest?.('summary');
    const details = summary?.parentElement;
    if (!details?.matches?.(accordionSelector) || event.defaultPrevented) return;
    event.preventDefault();
    const group = details.dataset.smoothGroup || details.getAttribute('name');
    if (group) {
      document.querySelectorAll('details[name]').forEach((item) => {
        if (item.getAttribute('name') !== group) return;
        item.dataset.smoothGroup = group;
        item.removeAttribute('name');
      });
      if (!details.open) document.querySelectorAll('details[data-smooth-group]').forEach((item) => {
        if (item !== details && item.dataset.smoothGroup === group && item.open) setAccordionOpen(item, false);
      });
    }
    const active = accordionAnimations.get(details);
    setAccordionOpen(details, active ? !active.shouldOpen : !details.open);
  });

  document.addEventListener('click', (event) => {
    document.querySelectorAll('[data-disclosure].is-open').forEach((node) => {
      if (!node.contains(event.target)) controllers.get(node)?.close?.({ restore: false });
    });
  });
  document.addEventListener('pointerover', hydrateSecondaryImage, { passive: true });
  // Legacy cards start the video when the pointer reaches the media; v2 cards
  // (Seed anatomy) treat the whole card as the hover target.
  const hoverTarget = (target) => {
    const card = target.closest?.('[data-product-card-v2]');
    const media = target.closest?.('[data-product-hover-media]') || card?.querySelector('[data-product-hover-media]');
    return media ? { media, boundary: card || media } : null;
  };
  document.addEventListener('pointerover', (event) => {
    const hit = hoverTarget(event.target);
    if (!hit || hit.boundary.contains(event.relatedTarget)) return;
    startCardVideo(hit.media);
  }, { passive: true });
  document.addEventListener('pointerout', (event) => {
    const hit = hoverTarget(event.target);
    if (!hit || hit.boundary.contains(event.relatedTarget)) return;
    stopCardVideo(hit.media);
  }, { passive: true });
  document.addEventListener('focusin', hydrateSecondaryImage);
  document.addEventListener('focusin', (event) => {
    const pending = event.target.closest?.('[data-reveal].is-pending');
    if (pending) revealImmediately(pending);
  });

  document.addEventListener('DOMContentLoaded', () => {
    initialize();
    document.querySelector('[data-form-feedback]')?.focus();
  });
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-product-hover-media]').forEach((media) => stopCardVideo(media));
    destroy(event.target);
  });
  document.addEventListener('shopify:section:select', (event) => {
    initialize(event.target);
    revealImmediately(event.target);
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    event.target.querySelectorAll('[data-disclosure].is-open, [data-dialog].is-open').forEach((node) => {
      controllers.get(node)?.close?.({ restore: false });
    });
  });
  document.addEventListener('shopify:block:select', (event) => {
    const hiddenPanel = event.target.closest('[data-disclosure-panel][hidden]');
    const disclosure = hiddenPanel?.closest('[data-disclosure]');
    controllers.get(disclosure)?.openPanel?.({ editorSelected: true });
    revealImmediately(event.target);
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    const disclosure = event.target.closest('[data-disclosure][data-editor-selected="true"]');
    controllers.get(disclosure)?.close?.({ restore: false });
  });
  prefersReducedMotion.addEventListener?.('change', handleMotionPreference);
  navigator.connection?.addEventListener?.('change', () => {
    if (!motionAllowed()) handleMotionPreference({ matches: true });
  });
  if ('MutationObserver' in window) {
    new MutationObserver(() => {
      if (!motionAllowed()) handleMotionPreference({ matches: true });
    }).observe(root, { attributes: true, attributeFilter: ['data-motion'] });
  }
  prefersReducedMotion.addEventListener?.('change', () => {
    if (prefersReducedMotion.matches) document.querySelectorAll('[data-product-hover-media].is-video-playing').forEach((media) => stopCardVideo(media));
  });
  finePointer.addEventListener?.('change', () => {
    if (!finePointer.matches) document.querySelectorAll('[data-product-hover-media].is-video-playing').forEach((media) => stopCardVideo(media));
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) document.querySelectorAll('[data-product-hover-media].is-video-playing').forEach((media) => stopCardVideo(media));
  });

  window.Reefline = { announce, getFocusable, FocusTrap, activateModal, deactivateModal, initialize, destroy, openDialog, closeDialog, setDialogDismissHandler };
})();
